import { useEffect } from 'react';

const CONTROL_SELECTOR = 'button, a[href], [role="button"], [role="tab"], [role="menuitem"]';
const DISABLED_SELECTOR = ':disabled, [aria-disabled="true"], .Mui-disabled';

// Delegate through document so portalled dialogs and newly rendered cards work too.
// Never capture the pointer or cancel an event: scrolling and normal clicks stay native.
export function attachTouchFeedback(doc) {
  const win = doc.defaultView;
  let held = null;
  const clear = () => {
    held?.element.removeAttribute('data-touch-pressed');
    held = null;
  };
  const down = event => {
    clear();
    if (!['touch', 'pen'].includes(event.pointerType) || event.isPrimary === false) return;
    const element = event.target?.closest?.(CONTROL_SELECTOR);
    if (!element || element.matches(DISABLED_SELECTOR) || element.closest('[aria-disabled="true"]')) return;
    held = { element, pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    element.setAttribute('data-touch-pressed', 'true');
  };
  const move = event => {
    if (!held || held.pointerId !== event.pointerId) return;
    // Once a gesture becomes a scroll/drag, don't reapply until the next press.
    if (Math.hypot(event.clientX - held.x, event.clientY - held.y) > 10) clear();
  };
  const release = event => {
    if (held?.pointerId === event.pointerId) clear();
  };
  const listeners = { pointerdown: down, pointermove: move, pointerup: release, pointercancel: release, lostpointercapture: release, scroll: clear, visibilitychange: clear };
  const options = { capture: true, passive: true };
  Object.entries(listeners).forEach(([type, listener]) => doc.addEventListener(type, listener, options));
  win?.addEventListener('blur', clear);
  return () => {
    clear();
    Object.entries(listeners).forEach(([type, listener]) => doc.removeEventListener(type, listener, options));
    win?.removeEventListener('blur', clear);
  };
}

export default function useTouchFeedback() {
  useEffect(() => attachTouchFeedback(document), []);
}
